import * as djangioActionCreators from "shared/djangio/action-creators"
import * as actionTypes from "shared/profiles/issueAdder/action-types"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

import { inIssueFilter, notInIssueFilter } from "shared/search/containers/Filter/FilterCreators"

import { fromJS } from "immutable"

import { issueAdderExtraFields } from "shared/profiles/constants/constants"
import { ISSUES_BULK_EMAIL_FIELD } from "shared/imports/sharedConstants"

/**
 *  grabs every issue this user has access to
 *
 * @param {QuorumDataType instance} qdt: representing the type of related resource
 * @param {String/Number} id: the id for the related resource
 * @param {Boolean} isMobile: true if the action creator is used on mobile, which will force legacy issue adder reducer behavior
 * @returns {Promise}: the issues associated with the related resource
 */
export const getIssues = ({ qdt, id, isMobile = false }) =>
    djangioActionCreators.resourceGetList(
        DjangIO.app.userdata.models.IssueManagement,
        actionTypes.getIssues,
        {
            [qdt.value === DjangIO.app.models.QuorumDataType.bulk_email.value
                ? ISSUES_BULK_EMAIL_FIELD
                : qdt.note_through_field]: id,
            dehydrate_extra: ["parent_name", "ancestors"],
        },
        {
            profileId: id,
            dataType: qdt.value,
            isMobile,
        },
    )

/**
 * adding a resource to an issue.
 *
 * @param {QuorumDataType instance} qdt: representing the type of related resource
 * @param {String/Number} id: the id for the related resource
 * @param {Object} issue: the issue this resource should be associated with
 * @param {Object, optional} overwriteActionTypes: if there are other actionTypes you want to use for this action
 * @param {Boolean} isMobile: true if the action creator is used on mobile, which will force legacy issue adder reducer behavior
 * @returns {Promise}: serialized issue
 */
export const addToIssue =
    ({ qdt, id, issue, overwriteActionTypes = false, isMobile = false, region = null }) =>
    async (dispatch, getState) => {
        const payload = {
            issue,
            profileId: id,
            dataType: qdt.value,
            isMobile,
        }

        if (!isMobile) {
            // get the issues for this issue adder from the store
            const ref = String(qdt.value) + "-" + id
            const issues = getState().issueAdder.getIn([ref, "issues"])

            // Find any ancestors (or this issue) not present in the store already
            // and add them to the payload
            // This implementation just adds the version that appears in its own _extra.ancestors list
            payload.addedIssues = issue._extra
                ? issue._extra.ancestors
                      .map((ancestor) =>
                          issues && issues.some((iss) => iss.get("id") === ancestor.id)
                              ? // this ancestor already exists
                                null
                              : // this ancestor is new and should be added
                                // mark it as a tentative addition so that we may revert the addition
                                fromJS(Object.assign(ancestor, { [issueAdderExtraFields.tentativelyAdded]: true })),
                      )
                      .filter((iss) => iss)
                : []
        }

        const response = await dispatch(
            djangioActionCreators.sendAction(
                DjangIO.app.userdata.models.IssueManagement.objects.get(issue.id),
                overwriteActionTypes || actionTypes.addToIssue,
                {
                    kwargs: {
                        id,
                        data_type: qdt.value,
                    },
                    action: "add_related",
                    payload: payload,
                    cacheInvalidationParams: {
                        endpoints: [DjangIO.newResourceFromDataType(qdt.value).endpoint],
                        shouldClearFn: (url) => {
                            const clearableKeys = inIssueFilter.defaultProps.allFilterKeys.concat(
                                notInIssueFilter.defaultProps.allFilterKeys,
                            )

                            clearableKeys.push("for_project_and_subprojects")

                            return clearableKeys.some((filterKey) => url.includes(filterKey))
                        },
                    },
                },
            ),
        )

        if (response) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.add_to_issue,
                description: `${qdt.label} added to issue: ${issue.name}`,
                quorumDataType: qdt.value,
                regions: [region],
            })
        }

        return response
    }

/**
 * removes a related resource from the corresponding m2m in the issue
 *
 * @param {QuorumDataType instance} qdt: representing the type of related resource
 * @param {String/Number} id: the id for the related resource
 * @param {Object} issue: the issue this resource should no longer be associated with
 * @param {Object, optional} overwriteActionTypes: if there are other actionTypes you want to use for this action
 * @param {Boolean} isMobile: true if the action creator is used on mobile, which will force legacy issue adder reducer behavior
 * @returns {Promise}: serialized issue
 */
export const removeFromIssue =
    ({ qdt, id, issue, overwriteActionTypes = false, isMobile = false, region = null }) =>
    async (dispatch) => {
        const response = await dispatch(
            djangioActionCreators.sendAction(
                DjangIO.app.userdata.models.IssueManagement.objects.get(issue.id),
                overwriteActionTypes || actionTypes.removeFromIssue,
                {
                    kwargs: {
                        id,
                        data_type: qdt.value,
                    },
                    action: "remove_related",
                    payload: {
                        issue,
                        profileId: id,
                        dataType: qdt.value,
                        isMobile,
                    },
                    cacheInvalidationParams: {
                        endpoints: [DjangIO.newResourceFromDataType(qdt.value).endpoint],
                        shouldClearFn: (url) => {
                            const clearableKeys = inIssueFilter.defaultProps.allFilterKeys.concat(
                                notInIssueFilter.defaultProps.allFilterKeys,
                            )

                            clearableKeys.push("for_project_and_subprojects")

                            return clearableKeys.some((filterKey) => url.includes(filterKey))
                        },
                    },
                },
            ),
        )

        if (response) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.remove_from_issue,
                description: `${qdt.label} removed from issue: ${issue.name}`,
                quorumDataType: qdt.value,
                regions: [region],
            })
        }
    }
